import React, { useEffect } from "react";
import { SortableTree, SimpleTreeItemWrapper } from "dnd-kit-sortable-tree";
import minusIcon from "../assets/minus-circle.svg";
import editIcon from "../assets/pen1.svg";
import options from "../assets/verticle-dots.svg";

export default function DataSortableTree({
  allIteams,
  selectedItemUid,
  onSelectItem,
  externalDropPreviewIndex,
  externalTreeDropPreview,
  setAllItems,
  onEditItem,
  onRemoveItem,
  isDragging = false,
}) {
  useEffect(() => {
    const container = document.querySelector('[data-menu-pages-root="true"]');
    if (!container) return undefined;

    const clearPreviewClasses = () => {
      container.classList.remove("menu-root-drop-end");
      container
        .querySelectorAll(".menu-root-drop-before")
        .forEach((node) => node.classList.remove("menu-root-drop-before"));
    };

    clearPreviewClasses();
    if (externalDropPreviewIndex == null) return clearPreviewClasses;

    const rootNodes = Array.from(
      container.querySelectorAll('[data-tree-depth="0"][data-menu-uid]')
    );

    if (rootNodes.length === 0 || externalDropPreviewIndex >= rootNodes.length) {
      container.classList.add("menu-root-drop-end");
      return clearPreviewClasses;
    }

    const target = rootNodes[externalDropPreviewIndex];
    if (target) {
      target.classList.add("menu-root-drop-before");
    }

    return clearPreviewClasses;
  }, [allIteams, externalDropPreviewIndex]);

  // Create items with placeholder inserted at drop position
  const itemsWithPlaceholder = React.useMemo(() => {
    if (!isDragging || externalDropPreviewIndex == null) {
      if (!isDragging || !externalTreeDropPreview?.targetUid) {
        return convertToTreeItems(allIteams);
      }
    }

    const converted = convertToTreeItems(allIteams);
    const placeholder = {
      id: "__placeholder__",
      __uid: "__placeholder__",
      value: "",
      isPlaceholder: true,
      children: [],
    };

    if (externalTreeDropPreview?.targetUid) {
      if (externalTreeDropPreview.mode === "child") {
        return insertPlaceholderAsChild(
          converted,
          externalTreeDropPreview.targetUid,
          placeholder
        );
      }
      return insertPlaceholderBefore(
        converted,
        externalTreeDropPreview.targetUid,
        placeholder
      );
    }

    // Insert placeholder at the drop index
    const result = [...converted];
    const insertIndex = Math.min(externalDropPreviewIndex, result.length);
    result.splice(insertIndex, 0, placeholder);
    return result;
  }, [allIteams, externalDropPreviewIndex, externalTreeDropPreview, isDragging]);

  return (
    <SortableTree
      items={itemsWithPlaceholder}
      onItemsChanged={(updatedItems) => {
        // Only update if we're not in the middle of an external drag/drop
        // This prevents double updates when dropping from outside
        if (isDragging) {
          return; // Let the parent's handleDragEnd handle the update
        }

        // Filter out placeholder before updating
        const filtered = updatedItems.filter(item => item.id !== "__placeholder__");
        const maxDepthEnforced = enforceMaxDepthTwo(filtered);
        const nextItems = assignOrder(maxDepthEnforced);

        // Prevent feedback loops: only commit when hierarchy/order changed.
        const prevSignature = JSON.stringify(buildTreeSignature(allIteams));
        const nextSignature = JSON.stringify(buildTreeSignature(nextItems));
        if (prevSignature !== nextSignature) {
          setAllItems(nextItems);
        }
      }}
      TreeItemComponent={(props) => (
        <TreeItem {...props} onEdit={onEditItem} onRemove={onRemoveItem} selectedItemUid={selectedItemUid}
          onSelectItem={onSelectItem} />
      )}
    />
  );
}

const TreeItem = React.forwardRef((props, ref) => {
  const { item, onEdit, onRemove, depth, selectedItemUid, onSelectItem } = props;

  // Render placeholder with special styling
  if (item.isPlaceholder) {
    return (
      <div
        ref={ref}
        className="menu-dnd-placeholder"
        data-menu-uid={item.__uid || ""}
        data-tree-depth={depth}
      >
        {/* <span style={{ color: "#3b82f6", fontSize: "14px", fontWeight: 500 }}>
          Drop here
        </span> */}
      </div>
    );
  }

  // item type dynamic

  const hasChildren = item.children && item.children.length > 0;

  let itemType = "Page";

  if (hasChildren) {
    itemType = "Dropdown";
  } else if (item.type === "primarybutton" || item.type === "secondarybutton" || item.type === "button") {
    itemType = "Button";
  } else if (item.type === "customlinks" || (!item.page_id && item.redirect_to)) {
    itemType = "Link";
  }
  return (
    <SimpleTreeItemWrapper
      {...props}
      ref={ref}
      indicator
      data-menu-uid={item.__uid || ""}
      data-tree-depth={depth}
      className={`${depth === 0 ? "menu-root-item" : ""} menu-tree-item ${selectedItemUid === item.__uid ? "active" : ""}`}
    >
      <div
        className="menu-tree-row"
        onClick={() => onSelectItem?.(item)}
        data-menu-uid={item.__uid || ""}
        data-tree-depth={depth}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div>{item.value}</div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span className="item-type">{itemType}</span>
          {/* <div className="item-option">
            <img src={options} alt="option" />
          </div> */}
          {/* <button
            className="btn-link me-2"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <img src={editIcon} alt="edit" style={{ width: 16, height: 16 }} />
          </button> */}

          {/* <button
            className="btn-link text-danger"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.id);
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <img
              src={minusIcon}
              alt="remove"
              style={{ width: 16, height: 16 }}
            />
          </button> */}
        </div>
      </div>
    </SimpleTreeItemWrapper>
  );
});

/* Convert only API data to the required format */
function convertToTreeItems(data) {
  const toTree = (items, depth = 0) => items.map((item) => ({
    ...item,
    id: item.__uid ?? item._id ?? item.page_id ?? item.id,
    value: item?.name?.en ?? item.name,
    canHaveChildren: depth < 2,
    children: item.children?.length ? toTree(item.children, depth + 1) : [],
  }));

  return toTree(data, 0);
}

function assignOrder(items, isSubMenu = false) {
  return items.map((item, index) => ({
    ...item,
    order: index + 1, // or index if you prefer 0-based
    menu_id: isSubMenu ? item.parentId : null,
    children: item.children?.length ? assignOrder(item.children, true) : [],
  }));
}

function buildTreeSignature(items = []) {
  return (items || []).map((item) => ({
    uid: String(item.__uid || item.id || item._id || item.page_id || ""),
    collapsed: Boolean(item.collapsed),
    children: buildTreeSignature(item.children || []),
  }));
}

function flattenToOneLevel(nodes = []) {
  const result = [];
  for (const node of nodes) {
    const children = Array.isArray(node.children) ? node.children : [];
    result.push({ ...node, children: [] });
    if (children.length) {
      result.push(...flattenToOneLevel(children));
    }
  }
  return result;
}

function enforceMaxDepthTwo(items = []) {
  return (items || []).map((root) => ({
    ...root,
    children: (root.children || []).map((child) => ({
      ...child,
      children: flattenToOneLevel(child.children || []),
    })),
  }));
}

function insertPlaceholderBefore(items, targetUid, placeholder) {
  const result = [];
  let inserted = false;

  for (const item of items) {
    if (item.__uid === targetUid) {
      result.push({ ...placeholder });
      result.push(item);
      inserted = true;
      continue;
    }

    if (item.children?.length) {
      const updatedChildren = insertPlaceholderBefore(
        item.children,
        targetUid,
        placeholder
      );
      if (updatedChildren !== item.children) {
        result.push({ ...item, children: updatedChildren });
        inserted = true;
      } else {
        result.push(item);
      }
    } else {
      result.push(item);
    }
  }

  return inserted ? result : items;
}

function insertPlaceholderAsChild(items, targetUid, placeholder) {
  return items.map((item) => {
    if (item.__uid === targetUid) {
      return {
        ...item,
        children: [...(item.children || []), { ...placeholder }],
      };
    }
    if (item.children?.length) {
      return {
        ...item,
        children: insertPlaceholderAsChild(item.children, targetUid, placeholder),
      };
    }
    return item;
  });
}
